import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Landmark,
  Car,
  PlaneTakeoff,
  Sparkles,
  Headphones,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import { getGuideCanonicalPath } from "@/i18n/legacySeoPaths";

type FeaturedCard = {
  key: string;
  icon: LucideIcon;
  to: string;
  titleKey: string;
  descKey: string;
  ctaKey: string;
};

type SecondaryCard = {
  key: string;
  icon: LucideIcon;
  to: (lang: string) => string;
  titleKey: string;
  descKey: string;
};

const FEATURED: FeaturedCard[] = [
  {
    key: "longStayVisa",
    icon: PlaneTakeoff,
    to: "/visa",
    titleKey: "homeGuides.featured.longStayVisa.title",
    descKey: "homeGuides.featured.longStayVisa.desc",
    ctaKey: "homeGuides.featured.longStayVisa.cta",
  },
  {
    key: "privileges",
    icon: Sparkles,
    to: "/privilege-points",
    titleKey: "homeGuides.featured.privileges.title",
    descKey: "homeGuides.featured.privileges.desc",
    ctaKey: "homeGuides.featured.privileges.cta",
  },
];

const SECONDARY: SecondaryCard[] = [
  {
    key: "bankAccount",
    icon: Landmark,
    to: (lang) => getGuideCanonicalPath("bankAccount", lang),
    titleKey: "guides.articles.bankAccount.title",
    descKey: "guides.articles.bankAccount.summary",
  },
  {
    key: "driving",
    icon: Car,
    to: (lang) => getGuideCanonicalPath("driving", lang),
    titleKey: "guides.articles.driving.title",
    descKey: "guides.articles.driving.summary",
  },
  {
    key: "memberService",
    icon: Headphones,
    to: () => "/guides",
    titleKey: "homeGuides.memberService.title",
    descKey: "homeGuides.memberService.desc",
  },
];

const HomeGuidesSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <section id="home-guides" className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-accent mb-3 block">
            {t("homeGuides.badge", "Member Guides")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
            {t("homeGuides.title", "Living in Thailand with Thailand Elite")}
          </h2>
          <p className="text-base text-muted-foreground font-body max-w-3xl mx-auto leading-relaxed">
            {t("homeGuides.intro")}
          </p>
        </motion.div>

        {/* Featured: 2 prominent cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 max-w-6xl mx-auto">
          {FEATURED.map(({ key, icon: Icon, to, titleKey, descKey, ctaKey }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <LocalizedLink
                to={to}
                className="group relative flex h-full flex-col gap-4 p-8 lg:p-10 rounded-xl bg-card border border-accent/30 hover:border-accent/60 hover:shadow-card transition-all overflow-hidden"
              >
                <div
                  aria-hidden
                  className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gradient-gold opacity-15 group-hover:opacity-25 transition-opacity"
                />
                <div className="relative z-10 flex items-center gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gradient-gold flex items-center justify-center shadow-gold">
                    <Icon className="h-7 w-7 text-accent-foreground" />
                  </div>
                  <h3 className="text-xl lg:text-2xl font-display font-bold text-foreground leading-snug group-hover:text-accent transition-colors">
                    {t(titleKey)}
                  </h3>
                </div>
                <p className="relative z-10 text-sm lg:text-base text-muted-foreground font-body leading-relaxed flex-1">
                  {t(descKey)}
                </p>
                <span className="relative z-10 inline-flex items-center gap-1.5 text-sm font-body font-semibold text-accent">
                  {t(ctaKey)}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </LocalizedLink>
            </motion.div>
          ))}
        </div>

        {/* Secondary: 3 standard cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 max-w-6xl mx-auto">
          {SECONDARY.map(({ key, icon: Icon, to, titleKey, descKey }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + i * 0.08 }}
            >
              <LocalizedLink
                to={to(lang)}
                className="group flex h-full flex-col gap-3 p-6 rounded-lg bg-background border border-border hover:border-accent/30 hover:shadow-card transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-md bg-gradient-gold flex items-center justify-center">
                    <Icon className="h-5 w-5 text-accent-foreground" />
                  </div>
                  <h3 className="text-base font-display font-semibold text-foreground leading-snug group-hover:text-accent transition-colors">
                    {t(titleKey)}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground font-body leading-relaxed flex-1">
                  {t(descKey)}
                </p>
                <span className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-accent">
                  {t("guides.readMore", "Read full guide")}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </LocalizedLink>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <LocalizedLink
            to="/guides"
            className="inline-flex items-center gap-2 text-sm font-body font-semibold text-accent hover:underline"
          >
            {t("homeGuides.browseAll", "Browse all 12 member guides")}
            <ArrowRight className="h-4 w-4" />
          </LocalizedLink>
        </div>
      </div>
    </section>
  );
};

export default HomeGuidesSection;
